import * as React from 'react';
import { Component, inject, deps, observer } from '../lib/component';
import { classes, media, style } from 'typestyle';
import { merge } from 'lodash';
import { percent, px, viewWidth } from 'csx';
import { centerCenter, horizontal, wrap } from 'csstips';
import { AddRemoveCompat } from '../components/compat/add-remove-compat';
import { PlayButtonCompat } from '../components/compat/play-button-compat';
import { FavouriteButtonCompat } from '../components/compat/favourite-button-compat';
import { Loading } from '../components/loading/loading';
import { FavouriteArtistButtonCompat } from '../components/compat/favourite-artist-button-compat';
import { BrandSponsoredLabelCompat } from '../components/compat/brand-sponsored-label-compat';
import { AccountLayout } from '../components/account/layout';
import { ReleaseTracks } from '../components/release-tracks';
import { ButtonPill } from '../components/button-pill';
import { ArtistTile } from '../components/artist-tile';
import { colorSlate, colorSubtle, colorWhite } from '../theme/color';
import { mediaDesktop, mediaMobileOnly, mediaTablet, mediaTabletOnly } from '../theme/media';
import { AccountCommunicationPage } from './account-communication';
import { AnalyticsController } from '../modules/analytics/controller';
import PlaylistTile, { CompactPlaylistTileStyles } from '../components/carousel/playlist-tile';
import PageAccountFavouritesListing from '../components/page-account-favourites-listing';
import MigrationRetriggerButton from '../components/channel/migration-retrigger-button';

export interface AccountFavouritesPageProps {
  i18n?: {
    loading: string;
    title: string;
    titleTracks: string;
    titleArtists: string;
    titlePlaylists: string;
  };
  analytics: AnalyticsController;
}

const TRACK_LISTING = 'track-listing';
const ARTIST_LISTING = 'artist-listing';
const PLAYLIST_LISTING = 'playlist-listing';

@inject(deps)
@observer
export class AccountFavouritesPage extends Component<AccountFavouritesPageProps> {
  render() {
    const { i18n = AccountFavouritesPage.i18n, analytics } = this.props;
    const model = this.props.model.page.favourites;
    const controller = this.props.controller.page.favourites;
    const { isMobile } = this.props.model.ui;
    const { preloadImage, preloadWaveformSvg } = this.props.controller.image;
    const { handleClickLink } = this.props.controller.ui;

    if (model.loading)
      return (
        <AccountLayout>
          <div className={AccountCommunicationPage.styles.containerLoading}>
            <Loading />
          </div>
        </AccountLayout>
      );

    return (
      <AccountLayout>
        <MigrationRetriggerButton>Missing your favourites?</MigrationRetriggerButton>
        <div
          className={classes(
            AccountFavouritesPage.styles.container,
            model.hasTracks && AccountFavouritesPage.styles.withTracks,
          )}
        >
          <PageAccountFavouritesListing
            className={TRACK_LISTING}
            title={i18n.titleTracks}
            isEmpty={!model.hasTracks}
            emptyText="You don't have any favourite tracks yet"
            emptyHref="/search"
            emptyCta="Search tracks"
            onClickLink={handleClickLink}
          >
            <ReleaseTracks
              model={model.infiniteTracks}
              AddRemove={AddRemoveCompat}
              PlayButton={PlayButtonCompat}
              Favourite={FavouriteButtonCompat}
              BrandSponsoredLabel={BrandSponsoredLabelCompat}
              isMobile={isMobile}
              onClickLink={handleClickLink}
              preloadWaveform={preloadWaveformSvg}
              analytics={analytics}
            />
            {model.hasMoreTracks && !model.loadingMoreTracks && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMoreTracks}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreTracks && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
          <PageAccountFavouritesListing
            className={ARTIST_LISTING}
            title={i18n.titleArtists}
            isEmpty={!model.hasArtists}
            emptyText="You don't have any favourite artists yet"
            emptyHref="/search"
            emptyCta="Search artists"
            onClickLink={handleClickLink}
          >
            <div className={AccountFavouritesPage.styles.grid}>
              {model.filteredArtists.map((artist, index) => (
                <ArtistTile
                  key={index.toString()}
                  artist={artist}
                  preload={preloadImage}
                  Favourite={FavouriteArtistButtonCompat}
                  onClickLink={handleClickLink}
                />
              ))}
            </div>
            {model.hasMoreArtists && !model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMoreArtists}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
          <PageAccountFavouritesListing
            className={PLAYLIST_LISTING}
            title={i18n.titlePlaylists}
            isEmpty={!model.hasPlaylists}
            emptyText="You don't have any favourite playlists yet"
            emptyHref="/browse/collection"
            emptyCta="Browse playlists"
            onClickLink={handleClickLink}
          >
            <div className={AccountFavouritesPage.styles.grid}>
              {model.filteredPlaylists.map((playlist, index) => (
                <PlaylistTile
                  className={CompactPlaylistTileStyles}
                  identity={playlist.uuid}
                  key={index.toString()}
                  slug={playlist.slug}
                  title={playlist.name}
                  images={playlist.track_images}
                  onClickLink={handleClickLink}
                />
              ))}
            </div>
            {model.hasMoreArtists && !model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <ButtonPill onClick={controller.loadMorePlaylists}>Load more</ButtonPill>
              </div>
            )}
            {model.loadingMoreArtists && (
              <div className={AccountFavouritesPage.styles.loadMore}>
                <Loading />
              </div>
            )}
          </PageAccountFavouritesListing>
        </div>
      </AccountLayout>
    );
  }

  static i18n = {
    loading: 'Loading...',
    title: 'Favourites',
    titleTracks: 'Favourite tracks',
    titleArtists: 'Favourite artists',
    titlePlaylists: 'Favourite playlists',
  };

  static styles = {
    container: style(
      mediaTablet({
        display: 'grid',
        gridTemplate: 'repeat(2, auto) / repeat(3, 1fr)',
        gridGap: px(20),
      }),
    ),
    withTracks: style(
      mediaTablet({
        $nest: {
          [`& > .${TRACK_LISTING}`]: {
            gridArea: '1/1/3/3',
          },
          [`& > .${ARTIST_LISTING}`]: {
            gridArea: '1/3/2/4',
          },
          [`& > .${PLAYLIST_LISTING}`]: {
            gridArea: '2/3/3/4',
          },
        },
      }),
    ),

    loading: style({
      height: px(100),
      ...centerCenter,
    }),
    body: style({
      padding: px(20),
      background: colorSlate.toString(),
      color: colorWhite.toString(),
    }),
    split: style(
      {
        ...horizontal,
        margin: 0,
      },
      mediaMobileOnly({
        flexDirection: 'column-reverse',
        padding: '0 20px',
      }),
    ),
    wrapper: style(
      {
        position: 'relative',
        height: percent(100),
        margin: 0,
      },
      mediaTablet({
        marginLeft: px(20),
      }),
    ),
    main: style(
      {
        overflow: 'hidden',
        flex: 2,
      },
      mediaMobileOnly({
        overflow: 'auto',
        width: percent(100),
        margin: 0,
        height: 'auto',
        flex: 'none',
      }),
    ),
    aside: style(
      {
        flex: 1,
      },
      mediaMobileOnly({
        width: percent(100),
        margin: 0,
        height: 'auto',
        flex: 'none',
      }),
    ),
    heading: style({
      margin: '30px 0',
    }),
    loadMore: style({
      ...centerCenter,
      padding: '20px',
    }),
    grid: style({
      ...horizontal,
      ...wrap,
      width: percent(100),
      $nest: {
        '&> *': {
          width: percent(50),
        },
      },
    }),
  };
}
